// Central SEO config + JSON-LD schema builders for Kurti Connection USA
export const SITE = {
  name: "Kurti Connection USA",
  legalName: "Kurti Connection",
  url: "https://kurticonnection.com",
  tagline: "Indian Ethnic Wear & Wedding Attire in the USA",
  description:
    "Kurti Connection USA is an Indian ethnic fashion boutique in Schaumburg, IL. Shop kurtis, sarees, lehengas, party wear, wedding-guest outfits, dupattas, blouses & jewelry — in-store near Chicago and shipping across the USA.",
  phone: "+12242461461",
  phoneDisplay: "+1 (224) 246-1461",
  whatsapp: "12242461461",
  accountUrl: "https://account.kurticonnectionusa.com",
  email: "hello@kurticonnection.com",
  address: {
    street: "183 West Golf Rd",
    city: "Schaumburg",
    region: "IL",
    postal: "60195",
    country: "US",
  },
  geo: { lat: 42.0455, lng: -88.0951 },
  hours: [
    { days: ["Monday"], open: "14:00", close: "19:00" },
    { days: ["Tuesday", "Wednesday", "Thursday", "Friday"], open: "11:30", close: "19:00" },
    { days: ["Saturday", "Sunday"], open: "11:30", close: "18:00" },
  ],
  defaultImage: "https://kurticonnection.com/og-default.png",
  // Same-day local delivery via DoorDash (within ~25 mi of the Schaumburg boutique)
  doordash: "https://www.doordash.com/convenience/store/50665776",
};

export const abs = (path = "/") => `${SITE.url}${path.startsWith("/") ? path : `/${path}`}`;

export const organizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE.name,
  url: SITE.url,
  logo: abs("/logo512.png"),
  email: SITE.email,
  telephone: SITE.phone,
});

export const localBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": "ClothingStore",
  "@id": `${SITE.url}/#store`,
  name: SITE.name,
  image: SITE.defaultImage,
  url: SITE.url,
  telephone: SITE.phone,
  email: SITE.email,
  priceRange: "$$",
  currenciesAccepted: "USD",
  address: {
    "@type": "PostalAddress",
    streetAddress: SITE.address.street,
    addressLocality: SITE.address.city,
    addressRegion: SITE.address.region,
    postalCode: SITE.address.postal,
    addressCountry: SITE.address.country,
  },
  geo: { "@type": "GeoCoordinates", latitude: SITE.geo.lat, longitude: SITE.geo.lng },
  areaServed: [
    "Schaumburg IL", "Hoffman Estates IL", "Palatine IL", "Arlington Heights IL",
    "Buffalo Grove IL", "Vernon Hills IL", "Lake Zurich IL", "Mundelein IL",
    "Crystal Lake IL", "Chicago IL", "South Barrington IL", "Barrington IL",
    "Elgin IL", "Elk Grove Village IL", "Cary IL", "Naperville IL",
    "Roselle IL", "Carpentersville IL", "Wheeling IL", "Long Grove IL",
    "Wisconsin", "Indiana",
  ].map((name) => ({ "@type": "City", name })),
  openingHoursSpecification: SITE.hours.map((h) => ({
    "@type": "OpeningHoursSpecification",
    dayOfWeek: h.days,
    opens: h.open,
    closes: h.close,
  })),
  areaServed: [
    "Schaumburg", "Hoffman Estates", "Arlington Heights", "Bloomingdale",
    "Streamwood", "Elgin", "Naperville", "Chicago", "United States",
  ],
});

export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE.name,
  url: SITE.url,
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE.url}/shop?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
});

export const productSchema = (product) => {
  if (!product) return null;
  const onSale = product.compare_at > product.price;
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.title,
    image: product.images || [],
    description: (product.description || `${product.title} — Indian ethnic wear at Kurti Connection USA.`).slice(0, 400),
    brand: { "@type": "Brand", name: SITE.name },
    offers: {
      "@type": "Offer",
      priceCurrency: "USD",
      price: Number(product.price || 0).toFixed(2),
      availability: product.stock_left === 0 ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
      url: abs(`/product/${product.slug}`),
      ...(onSale ? { priceValidUntil: `${new Date().getFullYear() + 1}-12-31` } : {}),
    },
    // Emit aggregateRating ONLY with real review data (Judge.me). Google penalizes
    // invented ratings — rating must be visible on the page and from genuine reviews.
    ...(product.reviews_count > 0 && product.rating
      ? { aggregateRating: { "@type": "AggregateRating", ratingValue: product.rating, reviewCount: product.reviews_count } }
      : {}),
  };
};

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: abs(it.path),
  })),
});

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});
